import React from 'react';

import { LocationHomeIcon, LockOffIcon } from '@vlabs/icons';
import { Tag } from '@vlabs/uikit/components/tag';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import cn from 'classnames';

import { DepartmentList } from '@vlabs/monokit/entities/lists/@x/departments';
import { useGetDepartment } from '@vlabs/api/artemy/client';
import type { DepartmentId } from '@vlabs/api/artemy/model';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { FoldableListItem } from '@vlabs/monokit/shared/ui/foldable-list-item';

import st from './DepartmentExtendedListItem.module.sass';
import { useDepartmentExtendedListItemTranslation } from './i18n';

export interface DepartmentExtendedListItemProps extends WithClassNameProp, WithDataTestid {
  departmentId: DepartmentId;
  actions?: React.ReactNode;
}

const displayName = 'DepartmentExtendedListItem';
const testId = buildTestid(displayName);

function DepartmentExtendedListItem({
  departmentId,
  actions = undefined,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: DepartmentExtendedListItemProps): React.JSX.Element {
  const { t } = useDepartmentExtendedListItemTranslation();

  const { data: department } = useGetDepartment(departmentId);

  const permissionTags = department?.permission_tags ?? [];
  const lists = department?.lists ?? [];
  const hasInfo = lists.length > 0 || permissionTags.length > 0;

  return (
    <FoldableListItem
      {...testId(dataTestId)}
      actions={actions}
      className={cn(st.Root, className)}
      heading={(
        <div className={st.Title}>
          <LocationHomeIcon />
          {department?.name ?? departmentId}
        </div>
        )}
      {...testId(dataTestId)}
    >
      {!!hasInfo && (
      <div className={st.Info}>
        {permissionTags.length > 0 && (
        <div className={st.InfoSection}>
          <span className={st.Text}>{t('info_permission_tags')}</span>
          <div className={st.Tags}>
            {permissionTags.map(({ permission_tag }) => (
              <Tag
                className={st.Tag}
                icon={<LockOffIcon />}
                key={permission_tag}
                kind="primary"
                shape="square"
                size="large"
                value={permission_tag}
              />
            ))}
          </div>
        </div>
        )}

        {lists.length > 0 && (
        <div className={st.InfoSection}>
          <span className={st.Text}>{t('info_lists')}</span>
          <div className={st.Lists}>
            {lists.map(({ list_id }) => (
              <DepartmentList
                className={st.List}
                key={list_id}
                listId={list_id}
              />
            ))}
          </div>
        </div>
        )}
      </div>
      )}
    </FoldableListItem>
  );
}

export { DepartmentExtendedListItem };
